/**
 * Offline-first Damage Assessment Service
 *
 * Handles creating, storing, and syncing damage assessments when offline.
 * Uses IndexedDB for local storage and syncs to the server when online.
 */

import { db, CachedDamageAssessment, OfflinePhoto } from './db';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';
import { resolveSyncableIncidentId } from './incident-offline.service';
import { mustDeferSend, originOnly, sendTenantOf } from './client-id';
import { syncBookkeeping } from './drain/drain-write-tracker';
import { compressImageBlob, rewriteFilenameForJpeg } from '@/utils/image-compression';
import { parseApiError, getErrorMessage } from '@/utils/api-errors';
import { isOfflineAddressSource, normalizeCreateAddressKeys, type OfflineAddressSource } from './address-keys';
import type {
  DamageAssessment,
  DamageAssessmentStatus,
  DamageSeverity,
  OwnershipType,
  StructureType,
} from '@/types/damage-assessment';

/** The only severities the UI can render. Cached rows type this as a bare `string`. */
const DAMAGE_SEVERITIES: readonly string[] = ['affected', 'minor', 'major', 'destroyed'];

/**
 * Offline damage-category key -> server boolean column.
 * Shared by BOTH directions (cache -> server payload, cache -> display shape) so
 * the two translations cannot drift apart.
 */
const SHORT_TO_SERVER_DAMAGE_KEY: Record<string, string> = {
  foundation: 'foundation_damage',
  roof: 'roof_damage',
  walls: 'wall_damage',
  windows: 'window_damage',
  electrical: 'electrical_damage',
  plumbing: 'plumbing_damage',
  hvac: 'hvac_damage',
  flood: 'flood_damage',
  fire: 'fire_damage',
};
const SERVER_DAMAGE_KEYS = new Set(Object.values(SHORT_TO_SERVER_DAMAGE_KEY));

// Generate a UUID for offline records
function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

export interface DamageAssessmentInput {
  incident_id?: string | null;
  zone_id?: string;
  /** Blank or absent = no address: the server fills it from the point after sync. */
  address?: string;
  /** 'entered' when a person typed it (the server never overwrites it). Ignored without an address. */
  address_source?: OfflineAddressSource;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  location?: { lat: number; lng: number } | null;
  affected_area?: unknown;
  property_type: string;
  structure_type?: string;
  ownership_type?: string;
  overall_severity: string;
  damage_categories: Record<string, { severity: string; notes: string }>;
  occupant_name?: string;
  occupant_phone?: string;
  occupant_email?: string;
  is_displaced?: boolean;
  is_habitable?: boolean;
  occupants_displaced?: number;
  estimated_damage_cost?: number;
  insurance_coverage?: boolean;
  fema_assistance_eligible?: boolean;
  /** FEMA Public Assistance category letter ('A'..'G','Z'). Never ''. */
  pa_category?: string;
  damage_description?: string;
  notes?: string;
  assigned_to?: string;
  metadata?: Record<string, unknown>;
}

export interface OfflinePhotoInput {
  blob: Blob;
  filename: string;
  content_type: string;
  category?: string;
  caption?: string;
  exif_data?: {
    lat?: number;
    lng?: number;
    timestamp?: string;
  };
}

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  photosFailed: number;
  errors: string[];
}

/**
 * Convert offline schema fields to server-expected field names.
 * Offline uses: property_type, overall_severity, is_displaced, damage_categories
 * Server expects: structure_type, severity, is_habitable, individual boolean flags
 *
 * NOTE: is_displaced and is_habitable are independent concepts —
 * a structure can be habitable but occupants displaced (e.g. evacuated for safety),
 * or uninhabitable with no displaced occupants (vacant property).
 * We use is_habitable directly when available, falling back to inverting is_displaced.
 */
function buildServerPayload(assessment: CachedDamageAssessment): Record<string, unknown> {
  // Coerce the money column defensively: rows cached before the mapServerToCache
  // fix below (or re-cached from any other consumer) can still hold the pg
  // NUMERIC string "185000.00", which the server's z.number() rejects.
  const cost = toFiniteNumber(assessment.estimated_damage_cost);

  const payload: Record<string, unknown> = {
    incident_id: assessment.incident_id || undefined,
    address: assessment.address,
    // Only alongside a non-blank address (see normalizeCreateAddressKeys on create).
    address_source: assessment.address?.trim() ? assessment.address_source : undefined,
    city: assessment.city,
    state: assessment.state,
    zip_code: assessment.zip_code,
    county: assessment.county,
    zone_id: assessment.zone_id,
    structure_type: assessment.structure_type || assessment.property_type || 'other',
    ownership_type: assessment.ownership_type,
    severity: assessment.overall_severity || 'affected',
    is_habitable: assessment.is_habitable ?? !assessment.is_displaced,
    notes: assessment.notes,
    damage_description: assessment.damage_description,
    occupants_displaced: assessment.occupants_displaced,
    estimated_damage_cost: cost,
    insurance_coverage: assessment.insurance_coverage,
    fema_assistance_eligible: assessment.fema_assistance_eligible,
    // Must be a bare enum letter or absent: the server schema is
    // z.enum(['A'..'G','Z']).optional(), so '' is an invalid_enum_value that 400s
    // the whole sync and strands the entire assessment, not just the category.
    pa_category: assessment.pa_category || undefined,
    assigned_to: assessment.assigned_to,
    offline_id: assessment.offline_id,
    metadata: assessment.metadata,
    client_created_at: assessment.client_created_at,
  };

  // Map location to GeoJSON Point
  if (assessment.location?.lat != null && assessment.location?.lng != null) {
    payload.location_point = {
      type: 'Point',
      coordinates: [assessment.location.lng, assessment.location.lat],
    };
  }

  // Include affected_area if present
  if (assessment.affected_area) {
    payload.affected_area = assessment.affected_area;
  }

  // Map damage_categories to individual boolean flags.
  // Handles BOTH short keys ('foundation', 'roof') and server keys ('foundation_damage', 'roof_damage')
  //
  // The emptiness check is required, not defensive noise: mapServerToCache stores
  // `{}` for every server-cached row (the backend returns no damage_categories
  // field), and `{}` is truthy. Without it, the "initialize all flags to false"
  // loop below zeroes foundation/roof/wall/window/electrical/plumbing/hvac/flood/
  // fire damage on EVERY sync of an edited assessment — wiping the assessor's
  // recorded damage findings. Omitting the keys entirely is safe: the backend
  // INSERT already defaults them.
  if (assessment.damage_categories && Object.keys(assessment.damage_categories).length > 0) {
    // Initialize all damage flags to false
    for (const serverKey of SERVER_DAMAGE_KEYS) {
      payload[serverKey] = false;
    }

    for (const [cat, catData] of Object.entries(assessment.damage_categories)) {
      // Determine the server field name: if cat is already a server key, use it directly;
      // otherwise look up from short key map
      const serverField = SERVER_DAMAGE_KEYS.has(cat) ? cat : SHORT_TO_SERVER_DAMAGE_KEY[cat];
      if (serverField && catData) {
        payload[serverField] = catData.severity !== 'none';
      }
    }
  }

  // Drop null / undefined / NaN entries before they reach the wire.
  //
  // WHY THIS MATTERS: updateDamageAssessmentSchema declares every nullable
  // column `.optional()` — NOT `.nullish()` — so an explicit `null` is an
  // invalid_type error and Zod rejects the ENTIRE PATCH with a 400. Rows cached
  // from the server carry `null` (not `undefined`) for every unset column
  // (zone_id, city, state, zip_code, county, notes, damage_description,
  // occupants_displaced, assigned_to, metadata). If this filter is removed, the
  // PATCH branch of syncAssessment 400s for essentially every server-cached
  // assessment: syncAssessment catches it, the row stays
  // is_synced=false/pending_changes=true, cacheFromServer then refuses to
  // overwrite it, and the record is permanently poisoned — an operator's
  // 'affected' -> 'destroyed' severity upgrade never reaches the server while
  // the pending badge sits in an error state that masks genuinely pending work.
  // (JSON.stringify already drops `undefined`, so this is only a behaviour
  // change for nulls; offline-created rows carry `undefined` and are unaffected.)
  return Object.fromEntries(
    Object.entries(payload).filter(
      ([, v]) => v !== null && v !== undefined && !(typeof v === 'number' && !Number.isFinite(v))
    )
  );
}

/**
 * Coerce a value that may have arrived as a Postgres NUMERIC string.
 * Returns undefined for null/empty/non-numeric input so the key is dropped
 * rather than sent as a string the server's z.number() would reject.
 */
function toFiniteNumber(value: unknown): number | undefined {
  if (value === null || value === undefined || value === '') return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * Map a server damage assessment response to the CachedDamageAssessment schema.
 * Server uses field names like structure_type, severity, location_point (GeoJSON);
 * cache uses property_type, overall_severity, location ({lat, lng}).
 */
function mapServerToCache(tenantId: string, server: Record<string, any>): CachedDamageAssessment {
  // Extract lat/lng from GeoJSON Point if present
  let location: { lat: number; lng: number } | null = null;
  if (server.location_point?.coordinates) {
    const [lng, lat] = server.location_point.coordinates;
    location = { lat, lng };
  }

  return {
    id: server.id,
    offline_id: server.offline_id,
    tenant_id: tenantId,
    incident_id: server.incident_id ?? null,
    zone_id: server.zone_id,
    assessment_number: server.assessment_number,
    address: server.address || '',
    address_source: server.address && isOfflineAddressSource(server.address_source) ? server.address_source : undefined,
    city: server.city,
    state: server.state,
    zip_code: server.zip_code,
    county: server.county,
    location,
    affected_area: server.affected_area,
    property_type: server.structure_type || server.property_type || 'other',
    structure_type: server.structure_type || server.property_type || 'other',
    ownership_type: server.ownership_type,
    overall_severity: server.severity || server.overall_severity || 'affected',
    status: server.status || 'draft',
    damage_categories: server.damage_categories || {},
    occupant_name: server.occupant_name,
    occupant_phone: server.occupant_phone,
    occupant_email: server.occupant_email,
    is_displaced: server.is_displaced ?? false,
    is_habitable: server.is_habitable,
    occupants_displaced: server.occupants_displaced,
    // estimated_damage_cost is DECIMAL(12,2); pg returns NUMERIC as a STRING
    // ("185000.00") and no type parser is registered, so the raw server JSON
    // carries a string here. Cached verbatim it would be typed `number` but hold
    // a string — breaking arithmetic/.toFixed() consumers and, on the next
    // PATCH, failing the server's z.number() validation. Coerce at the boundary.
    estimated_damage_cost: toFiniteNumber(server.estimated_damage_cost),
    insurance_coverage: server.insurance_coverage,
    fema_assistance_eligible: server.fema_assistance_eligible,
    // The list/detail SELECTs expose the FK'd category as `cc.code AS pa_category`.
    pa_category: server.pa_category,
    damage_description: server.damage_description,
    notes: server.notes,
    assigned_to: server.assigned_to,
    assessed_by: server.assessed_by,
    assessed_at: server.assessed_at,
    metadata: server.metadata,
    created_at: server.created_at || new Date().toISOString(),
    updated_at: server.updated_at || new Date().toISOString(),
    client_created_at: server.client_created_at,
    // Regrid parcel data from metadata
    regrid_owner: server.metadata?.regrid?.owner,
    regrid_assessed_value: server.metadata?.regrid?.assessed_value,
    regrid_market_value: server.metadata?.regrid?.market_value,
    regrid_year_built: server.metadata?.regrid?.year_built,
    regrid_lot_size_sqft: server.metadata?.regrid?.lot_size_sqft,
    regrid_building_sqft: server.metadata?.regrid?.building_sqft,
    regrid_land_use: server.metadata?.regrid?.land_use,
    regrid_zoning: server.metadata?.regrid?.zoning,
    regrid_parcel_id: server.metadata?.regrid?.parcel_id,
    // Sync tracking
    is_synced: true,
    synced_at: new Date().toISOString(),
    pending_changes: false,
    server_updated_at: server.updated_at,
    conflict_state: 'none',
  };
}

/**
 * Inverse of mapServerToCache: translate a cached row back into the server
 * `DamageAssessment` shape the UI renders from.
 *
 * WHY THIS EXISTS (do not replace call sites with `as unknown as DamageAssessment`):
 * the cache and the server disagree on field NAMES — the cache stores
 * `overall_severity` / `property_type`, the UI reads `severity` /
 * `structure_type`. Casting across that boundary leaves `severity` undefined on
 * every offline row, so `SEVERITY_CONFIG[a.severity] || SEVERITY_CONFIG.affected`
 * stamps the LOWEST severity badge ("Affected") on destroyed and major-damage
 * structures, the severity spine/pulse goes calm, and the worst-first sort
 * comparator returns 0 for every pair so nothing is ordered. An operator working
 * a disconnected neighbourhood then triages re-entry and FEMA escalation from a
 * list that shows total losses as the mildest category. `is_habitable` undefined
 * likewise renders a false "Not Habitable" flag on habitable structures.
 *
 * Severity is validated against the union rather than renamed-and-cast because
 * `overall_severity` is typed `string` and offline-created rows can hold anything.
 */
export function cachedToDamageAssessment(c: CachedDamageAssessment): DamageAssessment {
  const severity: DamageSeverity = DAMAGE_SEVERITIES.includes(c.overall_severity)
    ? (c.overall_severity as DamageSeverity)
    : 'affected';

  // Rebuild the nine server booleans from the cached category map, using the
  // same key table (and the same `!== 'none'` truth rule) as buildServerPayload.
  // NOTE: server-cached rows carry `damage_categories: {}` because the backend
  // returns the booleans and has no damage_categories column, so their flags
  // stay false here — offline-created rows are the ones this restores.
  const flags: Record<string, boolean> = {};
  for (const serverKey of SERVER_DAMAGE_KEYS) flags[serverKey] = false;
  for (const [cat, catData] of Object.entries(c.damage_categories || {})) {
    const serverField = SERVER_DAMAGE_KEYS.has(cat) ? cat : SHORT_TO_SERVER_DAMAGE_KEY[cat];
    if (serverField && catData) {
      flags[serverField] = catData.severity !== 'none';
    }
  }

  return {
    id: c.id,
    tenant_id: c.tenant_id,
    incident_id: c.incident_id || undefined,
    // Rows created offline have no server-assigned number yet; fall back to the
    // OFFLINE-XXXXXX convention rather than rendering "#undefined".
    assessment_number:
      c.assessment_number || `OFFLINE-${(c.offline_id || c.id).slice(-6).toUpperCase()}`,
    address: c.address,
    city: c.city,
    state: c.state,
    zip_code: c.zip_code,
    county: c.county,
    location_point: c.location
      ? { type: 'Point', coordinates: [c.location.lng, c.location.lat] }
      : undefined,
    affected_area: c.affected_area as DamageAssessment['affected_area'],
    structure_type: (c.structure_type || c.property_type || 'other') as StructureType,
    ownership_type: c.ownership_type as OwnershipType | undefined,
    severity,
    foundation_damage: flags.foundation_damage,
    roof_damage: flags.roof_damage,
    wall_damage: flags.wall_damage,
    window_damage: flags.window_damage,
    electrical_damage: flags.electrical_damage,
    plumbing_damage: flags.plumbing_damage,
    hvac_damage: flags.hvac_damage,
    flood_damage: flags.flood_damage,
    fire_damage: flags.fire_damage,
    // Match buildServerPayload: is_habitable and is_displaced are independent,
    // so only invert is_displaced when the explicit flag is absent.
    is_habitable: c.is_habitable ?? !c.is_displaced,
    occupants_displaced: c.occupants_displaced ?? 0,
    estimated_damage_cost: toFiniteNumber(c.estimated_damage_cost),
    insurance_coverage: c.insurance_coverage,
    fema_assistance_eligible: c.fema_assistance_eligible,
    damage_description: c.damage_description,
    notes: c.notes,
    // Server-side photo attachments are not mirrored into the cache; offline
    // photos live in the separate offlinePhotos table.
    attachments: [],
    status: (c.status || 'draft') as DamageAssessmentStatus,
    assigned_to: c.assigned_to,
    assessed_by: c.assessed_by,
    assessed_at: c.assessed_at,
    offline_id: c.offline_id,
    synced_at: c.synced_at,
    client_created_at: c.client_created_at,
    metadata: c.metadata || {},
    created_by: c.assessed_by || '',
    created_at: c.created_at,
    updated_at: c.updated_at,
  };
}

/** The cached address source after an edit: a changed address is a person's ('entered'). */
function addressSourceAfterEdit(
  existing: CachedDamageAssessment,
  updates: Partial<DamageAssessmentInput>
): OfflineAddressSource | undefined {
  const next = (updates.address ?? existing.address ?? '').trim();
  if (!next) return undefined;
  if (isOfflineAddressSource(updates.address_source)) return updates.address_source;
  if (updates.address !== undefined && updates.address.trim() !== (existing.address ?? '').trim()) return 'entered';
  return existing.address_source;
}

class DamageAssessmentOfflineService {
  /**
   * Create a new damage assessment (works offline)
   */
  async create(
    tenantId: string,
    input: DamageAssessmentInput,
    photos: OfflinePhotoInput[] = []
  ): Promise<CachedDamageAssessment> {
    const offlineId = generateOfflineId();
    const now = new Date().toISOString();
    // Blank address = no address (the server fills it after sync); a typed one keeps
    // its source so the server never overwrites it (migration 680).
    const address = (input.address ?? '').trim();
    const addressSource = address && isOfflineAddressSource(input.address_source) ? input.address_source : undefined;

    const assessment: CachedDamageAssessment = {
      id: offlineId,
      offline_id: offlineId,
      tenant_id: tenantId,
      incident_id: input.incident_id || null,
      zone_id: input.zone_id,
      address,
      ...(addressSource ? { address_source: addressSource } : {}),
      city: input.city,
      state: input.state,
      zip_code: input.zip_code,
      county: input.county,
      location: input.location || null,
      affected_area: input.affected_area,
      property_type: input.property_type,
      structure_type: input.structure_type || input.property_type,
      ownership_type: input.ownership_type,
      overall_severity: input.overall_severity,
      status: 'draft',
      damage_categories: input.damage_categories,
      occupant_name: input.occupant_name,
      occupant_phone: input.occupant_phone,
      occupant_email: input.occupant_email,
      is_displaced: input.is_displaced || false,
      is_habitable: input.is_habitable,
      occupants_displaced: input.occupants_displaced,
      estimated_damage_cost: input.estimated_damage_cost,
      insurance_coverage: input.insurance_coverage,
      fema_assistance_eligible: input.fema_assistance_eligible,
      pa_category: input.pa_category,
      damage_description: input.damage_description,
      notes: input.notes,
      assigned_to: input.assigned_to,
      metadata: input.metadata,
      client_created_at: now,
      created_at: now,
      updated_at: now,
      is_synced: false,
      pending_changes: true,
      conflict_state: 'none',
      ...originOnly(),
    };

    // Store the assessment
    await db.cachedDamageAssessments.add(assessment);

    // Store photos
    for (const photo of photos) {
      await this.addPhoto(offlineId, photo);
    }

    // Try to sync immediately if online
    if (networkService.isOnline()) {
      await this.syncAssessment(offlineId);
    }

    return assessment;
  }

  /**
   * Mirror a freshly-returned SERVER damage-assessment record into IndexedDB.
   *
   * WHY THIS EXISTS: the field form saves a draft as soon as the assessor leaves
   * the damage step. While online that draft is POSTed and only the SERVER uuid
   * is kept in component state — nothing was written locally. If the assessor
   * then loses signal before the final submit, every offline write keys off that
   * uuid and finds NO cached row: update() below returns null and Dexie's
   * update() resolves 0. Neither throws, so the form reported success while the
   * notes and the draft -> pending_review transition were silently discarded and
   * the assessment never reached the PDA/FEMA review queue.
   *
   * The row is stored as already-synced (mapServerToCache sets is_synced: true /
   * pending_changes: false) and keeps the server's own id, so a later edit syncs
   * via the PATCH branch of syncAssessment. Do NOT substitute create() here:
   * create() sets id === offline_id, which makes syncAssessment POST a DUPLICATE
   * assessment for the same structure.
   */
  async cacheServerRecord(
    tenantId: string,
    server: Record<string, any>
  ): Promise<CachedDamageAssessment | null> {
    if (!server?.id) return null;

    // Never clobber local work that has not reached the server yet — same
    // conflict rule cacheFromServer() applies.
    const existing = await db.cachedDamageAssessments.get(server.id);
    if (existing?.pending_changes) return existing;

    const mapped = mapServerToCache(tenantId, server);
    await db.cachedDamageAssessments.put(mapped);
    return mapped;
  }

  /**
   * Update an existing damage assessment (works offline)
   */
  async update(
    assessmentId: string,
    updates: Partial<DamageAssessmentInput>
  ): Promise<CachedDamageAssessment | null> {
    const existing = await db.cachedDamageAssessments.get(assessmentId);
    if (!existing) {
      // Try finding by offline_id
      const byOfflineId = await db.cachedDamageAssessments
        .where('offline_id')
        .equals(assessmentId)
        .first();
      if (!byOfflineId) return null;
      return this.update(byOfflineId.id, updates);
    }

    const updated: CachedDamageAssessment = {
      ...existing,
      ...updates,
      address: updates.address ?? existing.address,
      // A person's address edit makes it 'entered' (the server's trigger does the same).
      address_source: addressSourceAfterEdit(existing, updates),
      updated_at: new Date().toISOString(),
      is_synced: false,
      pending_changes: true,
    };

    await db.cachedDamageAssessments.put(updated);

    // Try to sync if online
    if (networkService.isOnline()) {
      await this.syncAssessment(updated.id);
    }

    return updated;
  }

  /**
   * Add a photo to an assessment (works offline)
   */
  async addPhoto(assessmentId: string, input: OfflinePhotoInput): Promise<OfflinePhoto> {
    // Resize + re-encode large images before storing. Field photos can easily
    // be 10-20 MB straight from the camera; trimming them here cuts both
    // IndexedDB pressure and upload time over cell/Starlink.
    const compressed = await compressImageBlob(input.blob);
    const finalContentType = compressed.content_type;
    const filename =
      finalContentType === 'image/jpeg' && input.content_type !== 'image/jpeg'
        ? rewriteFilenameForJpeg(input.filename)
        : input.filename;

    const photo: OfflinePhoto = {
      id: generateOfflineId(),
      assessment_id: assessmentId,
      blob: compressed.blob,
      filename,
      content_type: finalContentType,
      category: input.category,
      caption: input.caption,
      exif_data: input.exif_data,
      created_at: Date.now(),
      is_synced: false,
      ...originOnly(),
    };

    await db.offlinePhotos.add(photo);

    // Re-arm the parent assessment's photo-sync flags.
    //
    // WHY THIS MATTERS: `photos_synced` is only ever written by a sync attempt —
    // mapServerToCache/cacheServerRecord leave it UNDEFINED. The field form saves
    // its draft online (server uuid, row mirrored as is_synced: true), and if the
    // assessor then loses signal at the photos step every shot lands here against
    // that already-synced row. Without this flag the photos are invisible to every
    // sync path: getPending() skips the row (is_synced true), syncAssessment
    // returns early for the same reason, and the syncAll photo sweep used to
    // require a strict `photos_synced === false`. The photographic evidence behind
    // the FEMA claim and the re-entry decision then sits in IndexedDB until the
    // cache is wiped — while the form told the assessor it would upload on
    // reconnect. Looked up by primary key only: a row we cannot key exactly is
    // deliberately left alone (see the sweep in syncAll).
    const parent = await db.cachedDamageAssessments.get(assessmentId);
    if (parent) {
      const unsyncedCount = await db.offlinePhotos
        .where('assessment_id')
        .equals(assessmentId)
        .filter(p => !p.is_synced)
        .count();
      await db.cachedDamageAssessments.update(assessmentId, {
        photos_synced: false,
        unsynced_photo_count: unsyncedCount,
      });
    }

    return photo;
  }

  /**
   * Get photos for an assessment from local storage
   */
  async getPhotos(assessmentId: string): Promise<OfflinePhoto[]> {
    return db.offlinePhotos
      .where('assessment_id')
      .equals(assessmentId)
      .toArray();
  }

  /**
   * Delete a photo from local storage
   */
  async deletePhoto(photoId: string): Promise<void> {
    await db.offlinePhotos.delete(photoId);
  }

  /**
   * Get all cached damage assessments for a tenant
   */
  async getAll(tenantId: string, incidentId?: string | null): Promise<CachedDamageAssessment[]> {
    let query = db.cachedDamageAssessments.where('tenant_id').equals(tenantId);

    const results = await query.toArray();

    if (incidentId !== undefined) {
      return results.filter(a => a.incident_id === incidentId);
    }

    return results;
  }

  /**
   * Get a single assessment by ID
   */
  async getById(assessmentId: string): Promise<CachedDamageAssessment | undefined> {
    let assessment = await db.cachedDamageAssessments.get(assessmentId);
    if (!assessment) {
      // Try finding by offline_id
      assessment = await db.cachedDamageAssessments
        .where('offline_id')
        .equals(assessmentId)
        .first();
    }
    return assessment;
  }

  /**
   * Get pending (unsynced) assessments
   */
  async getPending(): Promise<CachedDamageAssessment[]> {
    // is_synced is written as a boolean (false/true). Dexie does NOT coerce
    // booleans to 0/1 in its index, so `.where('is_synced').equals(0)` matches
    // ZERO rows and offline assessments silently never sync (data loss). Use the
    // loose predicate filter — matches the correct sibling getPendingCount().
    return db.cachedDamageAssessments
      .filter(a => !a.is_synced)
      .toArray();
  }

  /**
   * Get count of pending items
   */
  async getPendingCount(): Promise<{ assessments: number; photos: number }> {
    const assessments = await db.cachedDamageAssessments
      .filter(a => !a.is_synced)
      .count();
    const photos = await db.offlinePhotos
      .filter(p => !p.is_synced)
      .count();
    return { assessments, photos };
  }

  /**
   * Cache assessments from the server for offline access
   * Uses conflict resolution strategy:
   * - If local has pending changes, preserve local version (user's work takes priority)
   * - If local is synced and server is newer, update from server
   * - If local is synced and server is older/same, keep local (already up to date)
   */
  async cacheFromServer(tenantId: string, incidentId?: string): Promise<{ cached: number; conflicts: number }> {
    const result = { cached: 0, conflicts: 0 };

    try {
      // Page through the WHOLE list — this is the only fill path for the offline
      // damage-assessment cache (Field list, useOfflineDamageAssessments).
      //
      // WHY: sending no `limit` inherited the list controller's DEFAULT OF 20,
      // and the query is ORDER BY created_at DESC. A team that pre-cached a
      // 180-assessment incident and then drove out of coverage carried only the
      // 20 NEWEST rows — the offline list, which is exactly what renders when
      // the network request fails, showed 20 of 180. Assessors re-surveyed
      // addresses that were already documented, double-counting structures in
      // the PDA totals that drive the FEMA declaration threshold.
      //
      // `limit` is hard-capped at 100 by the route's Zod schema (asking for more
      // is a 400, not a silent clamp), so a big incident MUST be paged. Page,
      // don't guess. Terminate off the server's own `totalPages` (the response
      // is the bare `{ data, total, page, limit, totalPages }` object) and cap
      // the iterations unconditionally so a contract change can never spin.
      const PAGE_SIZE = 100;
      const MAX_PAGES = 50; // 5,000 assessments
      let pageNo = 1;
      let hasMore = true;

      while (hasMore && pageNo <= MAX_PAGES) {
        const params = new URLSearchParams();
        if (incidentId) params.append('incident_id', incidentId);
        params.append('limit', String(PAGE_SIZE));
        params.append('page', String(pageNo));

        const response = await api.get<{ data: any[]; totalPages?: number }>(`/damage-assessments?${params}`);
        const serverAssessments = response.data || [];

        for (const serverAssessment of serverAssessments) {
          const mapped = mapServerToCache(tenantId, serverAssessment);
          const existing = await db.cachedDamageAssessments.get(serverAssessment.id);

          if (!existing) {
            // New assessment from server - cache it
            await db.cachedDamageAssessments.put(mapped);
            result.cached++;
          } else if (existing.pending_changes) {
            // Local has unsaved changes - preserve local version, store server snapshot for conflict resolution
            await db.cachedDamageAssessments.update(existing.id, {
              conflict_state: 'detected',
              server_updated_at: serverAssessment.updated_at,
              server_snapshot: serverAssessment,
            });
            result.conflicts++;
            console.info(`[DamageAssessment] Conflict detected for ${serverAssessment.id} - preserving local changes`);
          } else if (new Date(serverAssessment.updated_at) > new Date(existing.updated_at)) {
            // Server version is newer and local has no pending changes - update from server
            await db.cachedDamageAssessments.put(mapped);
            result.cached++;
          }
          // else: Local is synced and up-to-date, no action needed
        }

        // Primary termination: the server's own page count. Fall back to the
        // "short page" heuristic only when totalPages is missing/unusable, and
        // keep the short-page guard either way so a missing field can't loop.
        const totalPages = Number(response?.totalPages);
        const serverHasMorePages =
          Number.isFinite(totalPages) && totalPages > 0 ? pageNo < totalPages : true;

        hasMore = serverAssessments.length === PAGE_SIZE && serverHasMorePages;
        pageNo++;
      }

      if (hasMore) {
        // Cap tripped — never fail silently; the offline cache is incomplete.
        console.warn(
          `[DamageAssessment] Stopped after ${MAX_PAGES} pages (${result.cached} cached); ` +
            'server still reported more. Offline assessment cache may be incomplete.'
        );
      }
    } catch (error) {
      console.warn('[DamageAssessment] Failed to cache from server:', error);
      // Don't throw - offline mode should continue to work
    }

    return result;
  }

  /**
   * Sync a single assessment to the server
   */
  async syncAssessment(assessmentId: string): Promise<boolean> {
    const assessment = await this.getById(assessmentId);
    if (!assessment || assessment.is_synced) return true;
    // Another originator's (or an unknown originator's, or another tenant's) row is never
    // sent under this session, whichever entry point asked (sync sweep, Retry button):
    // it stays pending for its owner or the drain's handoff.
    if (mustDeferSend(assessment)) return false;

    try {
      let serverId = assessment.id;

      // Build server-compatible payload from offline schema
      let serverPayload = buildServerPayload(assessment);

      // Check if this is a new offline assessment
      if (assessment.offline_id && assessment.id === assessment.offline_id) {
        // A blank address is sent as NO address (the server fills it from the point);
        // rows queued before this fix hold address ''.
        serverPayload = normalizeCreateAddressKeys(serverPayload);
        // An offline-created incident's server id (P2-05), or wait for it.
        const resolved = await resolveSyncableIncidentId(assessment.incident_id);
        if (resolved.defer) return false;
        if (resolved.incidentId) serverPayload.incident_id = resolved.incidentId;
        // Create on server. A replay with the same offline_id returns the
        // existing assessment with 200 (P2-05), not a 409.
        const response = await api.post<{ data: { id: string } }>('/damage-assessments', serverPayload, sendTenantOf(assessment));

        serverId = response.data.id;

        // Update local record with server ID (mark data as synced; photos checked below).
        // Atomic delete+put so a crash between the two ops can't lose the assessment.
        await db.transaction('rw', db.cachedDamageAssessments, async () => {
          await db.cachedDamageAssessments.delete(assessment.id);
          await db.cachedDamageAssessments.put({
            ...assessment,
            id: serverId,
            incident_id: resolved.incidentId ?? assessment.incident_id,
            is_synced: true,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            last_error: undefined,
            photos_synced: true,
            unsynced_photo_count: 0,
          });
        });

        // Update photo references
        await db.offlinePhotos
          .where('assessment_id')
          .equals(assessment.id)
          .modify({ assessment_id: serverId });
      } else {
        // Update existing on server (exclude offline_id from update payload)
        const { offline_id: _, ...updatePayload } = serverPayload;
        await api.patch(`/damage-assessments/${assessment.id}`, updatePayload, sendTenantOf(assessment));

        await db.cachedDamageAssessments.update(assessment.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        });
      }

      // Sync photos BEFORE the status transition — this ORDER IS LOAD-BEARING.
      //
      // The server enforces the tenant's "minimum required photos" rule on the
      // draft -> pending_review transition, and it can only count attachments it
      // already holds. PATCHing status first therefore 400s INSUFFICIENT_PHOTOS for
      // every tenant with that setting on, even though the assessor did take the
      // photos — they are sitting in IndexedDB, one HTTP call away. The catch below
      // would then flag the row pending and it would be retried forever, so a real
      // damaged/collapsed structure never reaches the EOC review queue.
      // Tracked separately so a photo failure doesn't hide data success.
      const photoResult = await this.syncPhotos(serverId);
      await db.cachedDamageAssessments.update(serverId, {
        photos_synced: photoResult.ok && !photoResult.deferred,
        unsynced_photo_count: photoResult.failedPhotos + (photoResult.deferred ?? 0),
      });

      // Sync status transition if the offline assessment was submitted (not just drafted)
      if (assessment.status && assessment.status !== 'draft') {
        try {
          await api.patch(`/damage-assessments/${serverId}/status`, {
            status: assessment.status,
          }, sendTenantOf(assessment));
        } catch (statusError) {
          console.warn('[DamageAssessment] Status sync failed, assessment data saved but status remains draft on server:', statusError);
          // Revert sync flags so the record shows as pending and the user knows
          // their status transition did not reach the server. Surface the SERVER's
          // reason (e.g. "At least 2 photo(s) must be uploaded...") rather than
          // axios's generic "Request failed with status code 400" — otherwise the
          // assessor has no way to know the record is one photo away from clearing.
          // Sync bookkeeping, not an edit: the row keeps its originator's stamp even when the
          // failure signed the user out (a non-refreshable 401 logs out before it rejects) or
          // someone else signed in while the call was in flight.
          await db.cachedDamageAssessments.update(
            serverId,
            syncBookkeeping({
              is_synced: false,
              pending_changes: true,
              last_error: `Status sync failed: ${getErrorMessage(parseApiError(statusError))}`,
            })
          );
          return false;
        }
      }

      // Data and status are on the server; a photo failure alone is a partial
      // failure so syncAll retries the remaining photos.
      return photoResult.ok;
    } catch (error) {
      console.error('[DamageAssessment] Sync failed:', error);
      await db.cachedDamageAssessments.update(assessment.id, {
        last_error: (error as Error).message,
      });
      return false;
    }
  }

  /**
   * Sync photos for an assessment.
   * Returns { ok, failedPhotos, deferred } so the caller can track photo-sync state separately.
   * `deferred` counts photos left pending because they are not this session's to send (another
   * originator's photo, or a parent assessment that is): they are not failures.
   */
  async syncPhotos(assessmentId: string): Promise<{ ok: boolean; failedPhotos: number; deferred: number }> {
    const photos = await db.offlinePhotos
      .where('assessment_id')
      .equals(assessmentId)
      .filter(p => !p.is_synced)
      .toArray();

    let failedPhotos = 0;
    let deferred = 0;
    // The parent, when it is stored here, must be sendable too (its tenant and originator).
    const parent = await db.cachedDamageAssessments.get(assessmentId);
    if (parent && mustDeferSend(parent)) return { ok: true, failedPhotos: 0, deferred: photos.length };

    for (const photo of photos) {
      // Each photo carries its own stamp: only its originator's session uploads it.
      if (mustDeferSend(photo)) {
        deferred++;
        continue;
      }
      try {
        // Create FormData for upload
        const formData = new FormData();
        formData.append('photos', photo.blob, photo.filename);
        if (photo.category) formData.append('category', photo.category);
        if (photo.caption) formData.append('caption', photo.caption);
        if (photo.exif_data) formData.append('exif_data', JSON.stringify(photo.exif_data));

        // Must go through the multipart path. A FormData body sent on the plain
        // JSON path is destroyed by axios before it leaves the browser (the
        // photo Blob serializes to `{}`), multer sees no files, and the upload
        // 400s on every retry — the assessment syncs while its FEMA photo
        // evidence stays stranded in IndexedDB until the device is wiped.
        const response = await api.upload<{ data: Array<{ id: string; storage_key: string }> }>(
          `/damage-assessments/${assessmentId}/photos`,
          formData, sendTenantOf(photo)
        );

        // Server returns an array of PhotoAttachment objects
        const uploadedPhotos = Array.isArray(response.data) ? response.data : [];
        const uploadedPhoto = uploadedPhotos[0];

        await db.offlinePhotos.update(photo.id, {
          is_synced: true,
          server_url: uploadedPhoto?.storage_key,
          upload_error: undefined,
        });
      } catch (error) {
        console.error('[DamageAssessment] Photo sync failed:', error);
        failedPhotos++;
        await db.offlinePhotos.update(photo.id, {
          upload_error: (error as Error).message,
        });
      }
    }

    return { ok: failedPhotos === 0, failedPhotos, deferred };
  }

  /**
   * Sync all pending assessments and photos
   */
  async syncAll(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      photosFailed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    // Tenant guard: the api client injects the *ambient* X-Tenant-ID, so a
    // record created under Tenant A must not be synced while Tenant B is
    // active. Defer (leave it pending) until its own tenant is active again.
    const activeTenantId = useTenantStore.getState().activeTenant?.id;

    const pending = await this.getPending();

    for (const assessment of pending) {
      // Another user's assessment is never synced under this session.
      if (mustDeferSend(assessment)) continue;
      if (
        assessment.tenant_id != null &&
        activeTenantId != null &&
        assessment.tenant_id !== activeTenantId
      ) {
        continue;
      }
      // P2-05: a new assessment under an offline-created incident that has not
      // synced yet waits (its incident_id is not a server id yet).
      const isNew = !!assessment.offline_id && assessment.id === assessment.offline_id;
      if (isNew && (await resolveSyncableIncidentId(assessment.incident_id)).defer) continue;
      const success = await this.syncAssessment(assessment.id);
      if (success) {
        result.synced++;
      } else {
        // Distinguish between data failures and photo-only failures
        const refreshed = await this.getById(assessment.id);
        if (refreshed && refreshed.is_synced && refreshed.photos_synced === false) {
          // Data made it; only photos failed — count separately
          result.synced++;
          result.photosFailed += refreshed.unsynced_photo_count || 1;
          result.errors.push(`Photo sync failed for assessment at ${assessment.address}`);
        } else {
          result.failed++;
          result.errors.push(`Failed to sync assessment at ${assessment.address}`);
        }
      }
    }

    // Also retry photo sync for already-synced records that still have photos
    // sitting in IndexedDB.
    //
    // WHY THE PHOTOS TABLE DRIVES THIS, not just the flag: `photos_synced` is
    // undefined on any row mirrored straight from the server, so a strict
    // `photos_synced === false` filter permanently excluded the exact case this
    // sweep exists for — draft saved online, signal lost at the photos step,
    // signal back BEFORE the final submit. That row is is_synced (so the loop
    // above skips it) and photos_synced-undefined (so the old filter skipped it):
    // the damage photos never uploaded at all, despite the form promising they
    // would. The flag-based half is kept so rows flagged by an earlier failed
    // attempt still get cleared even once their photos are gone.
    const candidateIds = new Set<string>();
    const flagged = await db.cachedDamageAssessments
      .filter(a => a.is_synced === true && (a.photos_synced === false || (a.unsynced_photo_count ?? 0) > 0))
      .toArray();
    for (const a of flagged) candidateIds.add(a.id);
    const unsyncedPhotos = await db.offlinePhotos.filter(p => !p.is_synced).toArray();
    for (const p of unsyncedPhotos) candidateIds.add(p.assessment_id);

    for (const id of candidateIds) {
      const a = await db.cachedDamageAssessments.get(id);
      // No parent row = no tenant proof for these bytes. The api client stamps
      // the AMBIENT X-Tenant-ID, so uploading blind would post one county's
      // damage photos into whichever tenant happens to be active. Leave them.
      if (!a) continue;
      if (mustDeferSend(a)) continue;
      // Same cross-tenant deferral the assessment loop above applies.
      if (a.tenant_id != null && activeTenantId != null && a.tenant_id !== activeTenantId) continue;
      // Still-pending rows belong to the loop above: their assessment may not
      // exist server-side yet, so the photo URL would 404.
      if (!a.is_synced) continue;

      const photoResult = await this.syncPhotos(a.id);
      if (photoResult.ok) {
        // Photos left for their own originator keep the row flagged (they are still owed).
        if (!photoResult.deferred) {
          await db.cachedDamageAssessments.update(a.id, { photos_synced: true, unsynced_photo_count: 0 });
        }
      } else {
        await db.cachedDamageAssessments.update(a.id, {
          photos_synced: false,
          unsynced_photo_count: photoResult.failedPhotos,
        });
        result.photosFailed += photoResult.failedPhotos;
        result.errors.push(`Photo retry failed for assessment at ${a.address}`);
      }
    }

    result.success = result.failed === 0 && result.photosFailed === 0;
    return result;
  }

  /**
   * Clear all cached data (use with caution)
   */
  async clearCache(): Promise<void> {
    await db.cachedDamageAssessments.clear();
    await db.offlinePhotos.clear();
  }

  /**
   * Clear only synced data (keep pending)
   */
  async clearSyncedCache(): Promise<void> {
    await db.cachedDamageAssessments
      .filter(a => a.is_synced && !a.pending_changes)
      .delete();
    await db.offlinePhotos
      .filter(p => p.is_synced)
      .delete();
  }
}

export const damageAssessmentOfflineService = new DamageAssessmentOfflineService();
